import type { ButtonVariant } from '@lightspeed/design-system-react'
import type { Button } from '@lightspeed/unified-components-helios-theme/react'
import { useT } from '@transifex/react'
import type { ComponentProps } from 'react'
import { useCallback, useEffect, useState } from 'react'
import { FEATURE_SALE_ADDRESS_MODAL_REFACTOR } from '../../../../../../constants/wr-features'
import type { FulfilmentDetail } from '../../../../../../models/fulfilment'
import { useSaleManager } from '../../../../../../ng-compat/hooks'
import { useAppDispatch, useAppSelector } from '../../../../../../state/hooks'
import {
  selectSaleFulfilmentDetail,
  setFulfilmentDetails,
} from '../../../../../../state/slices/currentSale/currentSaleSlice'
import { getFeatureFromCache } from '../../../../../../util/feature-cache'
import { useSaleFulfilment } from '../../hook/useSaleFulfilment'
import {
  HEAP_DATA_TRACK_ADD_ADDRESS,
  HEAP_DATA_TRACK_EDIT_ADDRESS,
} from '../../../../sale-model.constant'
import { EditAddressButtons } from './EditAddressButton.type'
import type { EditAddressButtonProps } from './EditAddressButton.type'

export type UnifiedButtonAppearance = NonNullable<
  ComponentProps<typeof Button>['appearance']
>

const legacyVariantToUnifiedAppearance: Record<
  ButtonVariant,
  UnifiedButtonAppearance
> = {
  go: 'primary',
  no: 'danger',
  supplementary: 'secondary',
}

export const getUnifiedButtonAppearance = ({
  hasCustomText,
  isAddType,
  variant,
}: {
  hasCustomText: boolean
  isAddType: boolean
  variant?: EditAddressButtonProps['variant']
}): UnifiedButtonAppearance => {
  if (variant) return legacyVariantToUnifiedAppearance[variant]
  if (!hasCustomText) return isAddType ? 'ghost' : 'icon-secondary'
  return 'secondary'
}

export const useEditAddressButtonModel = ({
  type,
  isModalOpeningOnError,
  hasError,
  text,
  variant,
  onClick,
  onClose,
  onSave,
  fulfilmentDetail,
  onOpenModal,
  lineItemLocalId,
}: EditAddressButtonProps) => {
  const t = useT()
  const { isDisabled } = useSaleFulfilment(lineItemLocalId || '')
  const isAddType =
    type === EditAddressButtons.add || type === EditAddressButtons.addShipping
  const isAddressModalRefactorEnabled = getFeatureFromCache(
    FEATURE_SALE_ADDRESS_MODAL_REFACTOR
  )

  const [showSaleAddressModal, setShowSaleAddressModal] = useState(false)
  const dispatch = useAppDispatch()
  const saleManager = useSaleManager()
  const savedFulfilmentDetails = useAppSelector(selectSaleFulfilmentDetail)
  const fulfilmentDetails = fulfilmentDetail ?? savedFulfilmentDetails

  const handleModalClose = useCallback(
    (updatedFulfillmentDetails?: FulfilmentDetail) => {
      if (updatedFulfillmentDetails && !onSave) {
        dispatch(
          setFulfilmentDetails([
            { ...updatedFulfillmentDetails, shouldAddNewAddress: false },
          ])
        )
      }
      setShowSaleAddressModal(false)
      onClose?.()
    },
    [dispatch, onClose, onSave]
  )

  const handleModalSave = useCallback(
    (fulfilmentDetails: FulfilmentDetail[]) => {
      if (onSave) {
        onSave(fulfilmentDetails)
        return
      }
      dispatch(setFulfilmentDetails(fulfilmentDetails))
      saleManager.updateFulfillmentDetails(fulfilmentDetails)
    },
    [dispatch, onSave, saleManager]
  )

  const openModal = useCallback(() => {
    if (isAddressModalRefactorEnabled) {
      onOpenModal()
      return
    }
    setShowSaleAddressModal(true)
  }, [isAddressModalRefactorEnabled, onOpenModal])

  const handleClick = useCallback(() => {
    onClick?.()
    openModal()
  }, [onClick, openModal])

  useEffect(() => {
    if (hasError && isModalOpeningOnError) {
      openModal()
    }
  }, [hasError, isModalOpeningOnError, openModal])

  return {
    addAddressLabel: t('Add address', { _context: 'Action link' }),
    ariaLabel: isAddType
      ? t('Add address', { _context: 'Action link' })
      : t('Edit address', { _context: 'Action link' }),
    appearance: getUnifiedButtonAppearance({
      hasCustomText: Boolean(text),
      isAddType,
      variant,
    }),
    dataTrack:
      type === EditAddressButtons.edit
        ? HEAP_DATA_TRACK_EDIT_ADDRESS
        : HEAP_DATA_TRACK_ADD_ADDRESS,
    fulfilmentDetails,
    handleClick,
    handleModalClose,
    handleModalSave,
    isAddType,
    isAddressModalRefactorEnabled,
    isDisabled,
    showSaleAddressModal,
  }
}
